import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { usePanel } from 'contexts/panelContext';
import { Pair } from 'types/Pair';
import { Icon } from 'components/_new/Icon';
import { userDisplayName } from 'utils/userDisplayName';
import { useNavigate } from 'hooks/useNavigate';
import TodoItem from 'components/userPanel/TodoItem';
import './MobileBottomNavigation.css';

import { ReactComponent as HomeSvg } from '../../../assets/icons/new/home.svg';
import { ReactComponent as UsersSvg } from '../../../assets/icons/new/users.svg';
import { ReactComponent as CalendarFullSvg } from '../../../assets/icons/new/calendar-full.svg';
import { ReactComponent as FileChecklistSvg } from '../../../assets/icons/new/file-checklist.svg';
import { ReactComponent as BookSvg } from '../../../assets/icons/new/book.svg';

const ICON_COLOR = '#686a85';
const ICON_COLOR_ACTIVE = '#5B21B6';

type BottomSheetType = 'pairs' | 'sessions' | 'todos' | null;

const NavIcon: React.FC<{
	SvgComponent: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
	isActive: boolean;
}> = ({ SvgComponent, isActive }) => {
	return (
		<div
			style={{
				color: isActive ? ICON_COLOR_ACTIVE : ICON_COLOR,
				transition: 'color 0.2s',
				width: '20px',
				height: '20px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<SvgComponent style={{ width: '100%', height: '100%' }} />
		</div>
	);
};

const MobileBottomNavigation: React.FC = () => {
	const { t } = useTranslation();
	const { organizationName, programName } = useParams();
	const navigate = useNavigate({
		scroll: ['scrollWrapperRef', 'pageScrollWrapperRef'],
	});
	const { panelType, userPairs, todosData, panelScopePath } = usePanel();
	const location = useLocation();

	const [activeSheet, setActiveSheet] = useState<BottomSheetType>(null);
	const [isSheetVisible, setIsSheetVisible] = useState(false);
	const [selectedPairId, setSelectedPairId] = useState<number | null>(null);
	const [pairMenuView, setPairMenuView] = useState<'list' | 'menu'>('list');
	const [sheetStartY, setSheetStartY] = useState(0);
	const [dragOffset, setDragOffset] = useState(0);
	const [isAnimating, setIsAnimating] = useState(false);
	const sheetRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (activeSheet) {
			closeSheet();
		}
	}, [location.pathname]);

	const handleHandleTouchStart = (e: React.TouchEvent) => {
		setSheetStartY(e.touches[0].clientY);
		setIsAnimating(false);
	};

	const handleHandleTouchMove = (e: React.TouchEvent) => {
		if (!sheetRef.current) return;

		const currentY = e.touches[0].clientY;
		const diff = currentY - sheetStartY;

		if (diff > 0) {
			setDragOffset(diff);
		}
	};

	const handleHandleTouchEnd = (e: React.TouchEvent) => {
		const endY = e.changedTouches[0].clientY;
		const diff = endY - sheetStartY;
		const threshold = 80;

		setIsAnimating(true);

		if (diff > threshold) {
			setDragOffset(500);
			setTimeout(() => {
				closeSheet();
			}, 300);
		} else {
			setDragOffset(0);
		}
	};

	const totalTodosCount = useMemo(() => {
		const commonCount = todosData?.common?.length || 0;
		const pairCount =
			todosData?.pair?.reduce((sum, pairTodo) => {
				return sum + (pairTodo?.items?.length || 0);
			}, 0) || 0;
		return commonCount + pairCount;
	}, [todosData]);

	const openSheet = (sheet: BottomSheetType) => {
		// Jeśli sesje i jest tylko 1 para - od razu nawiguj
		if (sheet === 'sessions' && userPairs && userPairs.length === 1) {
			const pair = userPairs[0];
			navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${pair.id}/session`);
			return; // Nie otwieraj sheeta
		}

		setActiveSheet(sheet);
		setDragOffset(0);

		// Automatycznie otwórz menu dla pojedynczej pary
		if (sheet === 'pairs' && userPairs && userPairs.length === 1) {
			setSelectedPairId(userPairs[0].id);
			setPairMenuView('menu');
		} else {
			setSelectedPairId(null);
			setPairMenuView('list');
		}

		setTimeout(() => {
			setIsSheetVisible(true);
		}, 10);
	};

	const closeSheet = () => {
		setIsSheetVisible(false);
		setTimeout(() => {
			setActiveSheet(null);
			setSelectedPairId(null);
			setPairMenuView('list');
			setSheetStartY(0);
			setDragOffset(0);
			setIsAnimating(false);
		}, 300);
	};

	const getPairMenuItems = (pair: Pair) => {
		const { id } = pair;
		const lastSessionId = pair?.stats?.lastSessionId || null;
		const evaluationLink = lastSessionId
			? `${panelScopePath}/pair/${id}/session/${lastSessionId}/evaluation-mentee`
			: null;

		const pairTodoData = todosData?.pair?.find((pairTodo) => {
			return pairTodo.items?.some((item) => item.variables?.pairId === id);
		});
		const pairTodos = pairTodoData?.items || [];

		const hasContractAlert = pairTodos.some((todo) => todo.type === 'contract_initial_sign_required');
		const hasMenteePlanAlert = pairTodos.some((todo) => todo.type === 'contract_goals_sign_required');
		const hasSessionAlert = pairTodos.some((todo) => todo.type === 'arrange_first_session_date');
		const hasFinalReportAlert = pairTodos.some((todo) => todo.type === 'final_report_mentor');
		const hasEvaluationAlert = pairTodos.some((todo) => todo.type === 'evaluation_survey_required');

		return [
			{
				id: `pair-${id}-session`,
				icon: <Icon name="calendar-check" iconStyle="regular" className="text-base" />,
				label: t('userPanel.navigation.sessions'),
				command: () => {
					navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/session`);
					closeSheet();
				},
				hasAlert: hasSessionAlert,
			},
			{
				id: `pair-${id}-contract`,
				icon: <Icon name="file-signature" iconStyle="solid" className="text-base" />,
				label: t('userPanel.navigation.contract'),
				hasAlert: hasContractAlert,
				command: () => {
					navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/contract`);
					closeSheet();
				},
			},
			{
				id: `pair-${id}-plan`,
				icon: <Icon name="list" iconStyle="solid" className="text-base" />,
				label: t('userPanel.navigation.mentoringPlan'),
				hasAlert: hasMenteePlanAlert,
				command: () => {
					navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/mentee-plan`);
					closeSheet();
				},
			},
			{
				id: `pair-${id}-final-report`,
				icon: <Icon name="file-lines" iconStyle="regular" className="text-base" />,
				label: t('userPanel.navigation.finalReport'),
				hasAlert: hasFinalReportAlert,
				command: () => {
					navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/final-report`);
					closeSheet();
				},
			},
			evaluationLink
				? {
						id: `pair-${id}-evaluation`,
						icon: <Icon name="list-check" iconStyle="solid" className="text-base" />,
						label: t('userPanel.navigation.evaluation'),
						hasAlert: hasEvaluationAlert,
						command: () => {
							navigate({
								pathname: evaluationLink,
								hash: '#content',
							});
							closeSheet();
						},
				  }
				: null,
			{
				id: `pair-${id}-process-summary`,
				icon: <Icon name="check" iconStyle="solid" className="text-base" />,
				label: t('userPanel.navigation.processSummary'),
				command: () => {
					navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/process-summary`);
					closeSheet();
				},
			},
			{
				id: `pair-${id}-materials`,
				icon: <Icon name="clipboard" iconStyle="regular" className="text-base" />,
				label: t('userPanel.navigation.userFiles'),
				command: () => {
					navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/userfiles`);
					closeSheet();
				},
			},
			{
				id: `pair-${id}-pair-details`,
				icon: <Icon name="address-card" iconStyle="regular" className="text-base" />,
				label:
					pair.application?.applicationRole === 'mentee'
						? t('userPanel.navigation.pairDataForMentees')
						: t('userPanel.navigation.pairDataForMentors'),
				command: () => {
					navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/pair-details`);
					closeSheet();
				},
			},
		].filter(Boolean);
	};

	const getPairLabel = (pair: Pair): string => {
		const menteeUser = pair.menteeApplication?.programMembership?.user || null;
		const mentorUser = pair.mentorApplication?.programMembership?.user || null;

		if (menteeUser) {
			return userDisplayName(menteeUser);
		}
		if (mentorUser) {
			return userDisplayName(mentorUser);
		}
		return `Para #${pair.id}`;
	};

	const renderPairsList = () => {
		if (!userPairs || userPairs.length === 0) {
			return (
				<div className="text-center py-8 text-gray-500">
					<Icon name="hourglass" iconStyle="regular" className="text-4xl mb-3" />
					<p>{t('userPanel.navigation.noPairs')}</p>
				</div>
			);
		}

		return (
			<div className="space-y-2">
				{userPairs.map((pair) => {
					const label = getPairLabel(pair);

					return (
						<button
							type="button"
							key={pair.id}
							onClick={() => {
								setSelectedPairId(pair.id);
								setPairMenuView('menu');
							}}
							className="mobile-bottom-nav-list-item"
						>
							<span className="font-medium text-gray-800">{label}</span>
							<div className="flex items-center gap-2">
								<Icon name="chevron-up" iconStyle="solid" style={{ color: ICON_COLOR }} />
							</div>
						</button>
					);
				})}
			</div>
		);
	};

	const renderPairMenu = () => {
		const pair = userPairs?.find((p) => p.id === selectedPairId);
		if (!pair) return null;

		const menuItems = getPairMenuItems(pair);
		const label = getPairLabel(pair);

		return (
			<div>
				{userPairs && userPairs.length > 1 && (
					<>
						<button
							type="button"
							onClick={() => {
								setSelectedPairId(null);
								setPairMenuView('list');
							}}
							className="mobile-bottom-nav-back-button"
						>
							<Icon
								name="chevron-left"
								iconStyle="solid"
								className="text-base"
								style={{ color: ICON_COLOR }}
							/>
							<span>{t('misc.goBack')}</span>
						</button>
						<h3 className="font-semibold text-lg mb-4 text-gray-800">{label}</h3>
					</>
				)}

				<div className="space-y-1">
					{menuItems
						.filter((item): item is NonNullable<typeof item> => item !== null)
						.map((item) => (
							<button
								type="button"
								key={item.id}
								onClick={() => item.command?.()}
								className="mobile-bottom-nav-menu-item"
							>
								<div className="flex items-center gap-3">
									{item.icon}
									<span className="text-gray-800">{item.label}</span>
								</div>
							</button>
						))}
				</div>
			</div>
		);
	};

	const renderSessionsList = () => {
		if (!userPairs || userPairs.length === 0) {
			return (
				<div className="text-center py-8 text-gray-500">
					<p>{t('userPanel.navigation.noPairs')}</p>
				</div>
			);
		}

		return (
			<div className="space-y-2">
				{userPairs.map((pair) => {
					const label = getPairLabel(pair);

					return (
						<button
							type="button"
							key={pair.id}
							onClick={() => {
								navigate(
									`/panel/${organizationName}/${programName}/${panelType}/pair/${pair.id}/session`
								);
								closeSheet();
							}}
							className="mobile-bottom-nav-list-item"
						>
							<div className="flex items-center gap-3">
								<Icon
									name="calendar"
									iconStyle="regular"
									style={{ color: ICON_COLOR }}
									className="text-base"
								/>
								<span className="font-medium text-gray-800">{label}</span>
							</div>
							<Icon name="chevron-right" iconStyle="solid" style={{ color: ICON_COLOR }} />
						</button>
					);
				})}
			</div>
		);
	};

	const renderTodosList = () => {
		const hasCommonTodos = todosData?.common && todosData.common.length > 0;
		const hasPairTodos = todosData?.pair && todosData.pair.some((p) => p.items && p.items.length > 0);

		if (!hasCommonTodos && !hasPairTodos) {
			return (
				<div className="text-center py-8 text-gray-500">
					<Icon name="check-circle" iconStyle="regular" className="text-4xl mb-3 text-green-500" />
					<p>{t('userPanel.todo.noNotification')}</p>
				</div>
			);
		}

		return (
			<div>
				{hasCommonTodos && (
					<>
						<div className="space-y-2">
							<ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
								{todosData.common.map(({ key, ...todo }) => (
									<TodoItem {...todo} isClickableWrapper />
								))}
							</ul>
						</div>
						{hasPairTodos && <div className="mobile-bottom-nav-todo-separator" />}
					</>
				)}

				{todosData?.pair?.map(({ name, items }, i) =>
					items.length > 0 ? (
						<div key={name}>
							{i > 0 && <div className="mobile-bottom-nav-todo-separator" />}
							<div className="mobile-bottom-nav-todo-group-header">
								<div className="flex items-center gap-2">
									<Icon
										name="user"
										iconStyle="solid"
										className="text-sm"
										style={{ color: ICON_COLOR }}
									/>
									<span className="text-sm font-semibold text-gray-700">
										{t('userPanel.todo.pairWith')}:{' '}
										<span className="font-bold text-gray-900">{name}</span>
									</span>
								</div>
							</div>
							<div className="space-y-2">
								<ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
									{items.map(({ key, ...todo }) => (
										<TodoItem key={key} isClickableWrapper {...todo} />
									))}
								</ul>
							</div>
						</div>
					) : null
				)}
			</div>
		);
	};

	const renderBottomSheet = () => {
		if (!activeSheet) return null;

		let title = '';
		let content = null;

		if (activeSheet === 'pairs') {
			if (pairMenuView === 'menu' && selectedPairId) {
				const pair = userPairs?.find((p) => p.id === selectedPairId);
				if (pair) {
					title = userPairs && userPairs.length > 1 ? t('userPanel.navigation.pairing') : getPairLabel(pair);
				}
				content = renderPairMenu();
			} else {
				title =
					userPairs && userPairs.length > 1
						? t('userPanel.navigation.pairing')
						: t('userPanel.navigation.yourPair');
				content = renderPairsList();
			}
		} else if (activeSheet === 'sessions') {
			title = t('userPanel.navigation.sessions');
			content = renderSessionsList();
		} else if (activeSheet === 'todos') {
			title = t('userPanel.todo.header');
			content = renderTodosList();
		}

		const getSheetTransition = (isAnimating: boolean, dragOffset: number): string => {
			if (!isAnimating) {
				return 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
			}
			if (dragOffset === 0) {
				return 'transform 0.3s ease-out';
			}
			return 'none';
		};
		return (
			<>
				<div
					role="button"
					tabIndex={0}
					className={`mobile-bottom-nav-backdrop ${
						isSheetVisible ? 'mobile-bottom-nav-backdrop-visible' : ''
					}`}
					onClick={closeSheet}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							closeSheet();
						}
					}}
					aria-label="Close menu"
				/>
				<div
					className={`mobile-bottom-nav-sheet ${isSheetVisible ? 'mobile-bottom-nav-sheet-visible' : ''}`}
					ref={sheetRef}
					style={{
						transform: isSheetVisible ? `translateY(${dragOffset}px)` : 'translateY(100%)',
						transition: getSheetTransition(isAnimating, dragOffset),
					}}
				>
					<div
						className="mobile-bottom-nav-handle"
						onTouchStart={handleHandleTouchStart}
						onTouchMove={handleHandleTouchMove}
						onTouchEnd={handleHandleTouchEnd}
					>
						<div className="mobile-bottom-nav-handle-bar" />
					</div>
					<div className="mobile-bottom-nav-header">
						<h2 className="text-xl font-bold text-gray-800">{title}</h2>
						<button type="button" onClick={closeSheet} className="mobile-bottom-nav-close-button">
							<Icon name="times" iconStyle="solid" className="text-xl" />
						</button>
					</div>
					<div className="mobile-bottom-nav-content" ref={contentRef}>
						{content}
					</div>
				</div>
			</>
		);
	};

	// Sprawdź czy aktualnie jesteśmy na stronie głównej
	const isOnHomePage = location.pathname === `/panel/${organizationName}/${programName}/${panelType}`;
	const isOnKnowledgeBase = location.pathname.includes('/knowledge-base');

	// Etykiety dla przycisków
	const pairsLabel =
		!userPairs || userPairs.length === 1 ? t('userPanel.navigation.yourPair') : t('userPanel.navigation.pairing');

	return (
		<>
			<div className="mobile-bottom-padder" />
			<div className="mobile-bottom-nav">
				<div className="mobile-bottom-nav-grid-five">
					{/* Główna */}
					<button
						type="button"
						onClick={() => {
							navigate(`/panel/${organizationName}/${programName}/${panelType}`);
						}}
						className="mobile-bottom-nav-button-compact"
					>
						<NavIcon SvgComponent={HomeSvg} isActive={isOnHomePage} />
						<span
							className="mobile-bottom-nav-label-compact"
							style={{ color: isOnHomePage ? ICON_COLOR_ACTIVE : ICON_COLOR }}
						>
							{t('userPanel.navigation.home')}
						</span>
					</button>

					{/* Pary */}
					<button
						type="button"
						onClick={() => {
							if (!userPairs || userPairs.length === 0) {
								return;
							}
							if (userPairs.length === 1) {
								setSelectedPairId(userPairs[0].id);
								setPairMenuView('menu');
							}
							openSheet('pairs');
						}}
						className="mobile-bottom-nav-button-compact"
						disabled={!userPairs || userPairs.length === 0}
					>
						<NavIcon SvgComponent={UsersSvg} isActive={activeSheet === 'pairs'} />
						<span
							className="mobile-bottom-nav-label-compact"
							style={{ color: activeSheet === 'pairs' ? ICON_COLOR_ACTIVE : ICON_COLOR }}
						>
							{pairsLabel}
						</span>
					</button>

					{/* Sesje */}
					<button
						type="button"
						onClick={() => openSheet('sessions')}
						className="mobile-bottom-nav-button-compact"
						disabled={!userPairs || userPairs.length === 0}
					>
						<NavIcon SvgComponent={CalendarFullSvg} isActive={activeSheet === 'sessions'} />
						<span
							className="mobile-bottom-nav-label-compact"
							style={{ color: activeSheet === 'sessions' ? ICON_COLOR_ACTIVE : ICON_COLOR }}
						>
							{t('userPanel.navigation.sessions')}
						</span>
					</button>

					{/* Do zrobienia */}
					<button
						type="button"
						onClick={() => openSheet('todos')}
						className="mobile-bottom-nav-button-compact"
					>
						<NavIcon SvgComponent={FileChecklistSvg} isActive={activeSheet === 'todos'} />
						<span
							className="mobile-bottom-nav-label-compact"
							style={{ color: activeSheet === 'todos' ? ICON_COLOR_ACTIVE : ICON_COLOR }}
						>
							{t('userPanel.todo.header')}
						</span>
						{totalTodosCount > 0 && (
							<span className="mobile-bottom-nav-badge-absolute">{totalTodosCount}</span>
						)}
					</button>

					{/* Wiedza */}
					<button
						type="button"
						onClick={() => {
							navigate(`/panel/${organizationName}/${programName}/${panelType}/knowledge-base`);
						}}
						className="mobile-bottom-nav-button-compact"
					>
						<NavIcon SvgComponent={BookSvg} isActive={isOnKnowledgeBase} />
						<span
							className="mobile-bottom-nav-label-compact"
							style={{ color: isOnKnowledgeBase ? ICON_COLOR_ACTIVE : ICON_COLOR }}
						>
							{t('userPanel.navigation.files')}
						</span>
					</button>
				</div>
			</div>

			{renderBottomSheet()}
		</>
	);
};

export default MobileBottomNavigation;
